import { useDataDog } from '@/providers/Datadog.provider';
import { useClientData } from '@/providers/ClientData.provider';
import { useSession } from '@/providers/Session.provider';
import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router';

/*

message will be something like 'Contact clicked' or 'CAD selected under Currency'
payload contains extra information for filtering in the form of { label, value }
  -> having both label and value is not required. e.g., buttons don't need a value 

*/
type HookProps = {
  message: string;
  payload: object;
  actionType: 'Navigation' | 'Button' | 'Option' | 'Input' | 'End';
};

export function useDatadogLog() {
  const { logMessage } = useDataDog();
  const { response } = useClientData();
  const { sessionId, baseId } = useSession();
  const location = useLocation();

  const sessionRef = useRef<string | null>(sessionId);
  const baseRef = useRef<string | null>(baseId);
  const locationRef = useRef<string>(location.pathname);
  useEffect(() => {
    sessionRef.current = sessionId;
    baseRef.current = baseId;
    locationRef.current = location.pathname;
  }, [sessionId, baseId]);

  const datadogLog = useCallback(
    ({ message, payload, actionType }: HookProps) => {
      const currentSession = sessionRef.current;
      const currentBase = baseRef.current;
      const currentLocation = locationRef.current;
      if (!currentSession) return;
      const messagePayload = {
        user: response.LoginUserName,
        accountId: response.AccountID,
        sessionId: currentSession,
        baseId: currentBase,
        page: currentLocation,
        actionType,
        portal: 'client-dashboard',
        ...(actionType === 'End' && { session_duration: Date.now() - Number(currentBase) }),
        payload,
      };

      const prefix = currentSession ? `[${currentSession}]: ` : '';

      logMessage(`${prefix}${message}`, 'info', messagePayload);
    },
    [logMessage, response],
  );

  return { datadogLog };
}
